<style scoped>
.mc-toast {
	position: fixed;
	bottom: 20px;
	right: 20px;
	z-index: 2000;
	max-width: 360px;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	background-color: #721c24;
	color: #fff;
	border-radius: 0.375rem;
	padding: 0.75rem 1rem;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.mc-toast .btn-close {
	filter: invert(1);
}
.progress-bar {
	background-color: #0d6efd;
	transition: width 0.3s ease;
}
.wizard-header-logo {
	height: 36px;
	width: 36px;
	border-radius: 50%;
	background-color: #ffffff;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	overflow: hidden;
	border: 1px solid #dee2e6;
}
.wizard-header-logo img {
	height: 100%;
	width: 100%;
	object-fit: contain;
}
/* Viewport-locked shell: no page scroll; screens fit available height.
   JS measures the visible viewport (mobile URL bar / keyboard aware) and
   scales the whole wizard via transform; --mc-scale feeds inner max-widths. */
.mc-shell {
	height: 100vh;
	height: 100dvh;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	--mc-scale: 1;
}
.mc-fit {
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
	flex-direction: column;
	transform-origin: top center;
}
.mc-fixed {
	flex-shrink: 0;
}
.mc-screen {
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
</style>
<template>
	<router-view v-slot="{ Component }">
		<LoadingOverlay :is-loading="isLoading" :loading-text="t('loading')">
			<template v-if="!isLoading">
				<!-- Unknown token -->
				<div
					v-if="store.notFound"
					class="container-fluid px-3 py-5 text-center"
				>
					<i class="bi bi-search fs-1 text-muted d-block mb-3"></i>
					<h2 class="h4 mb-3">{{ t("not_found_title") }}</h2>
					<p class="text-muted mx-auto" style="max-width: 420px">
						{{
							t("err_not_found", {
								district_name: org.name,
								district_phone: org.phone,
							})
						}}
					</p>
				</div>
				<!-- Load failed -->
				<div
					v-else-if="!store.state"
					class="container-fluid px-3 py-5 text-center"
				>
					<i class="bi bi-exclamation-circle fs-1 text-muted d-block mb-3"></i>
					<p class="text-muted">{{ t("err_load") }}</p>
				</div>
				<template v-else>
					<div
						ref="shellRef"
						class="container-fluid px-3 py-2 mc-shell"
						:style="{ '--mc-scale': scale }"
					>
						<div ref="fitRef" class="mc-fit">
							<header
								class="mb-2 pb-2 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2 mc-fixed"
							>
								<div class="d-flex align-items-center gap-2">
									<div v-if="showHeaderLogo" class="wizard-header-logo">
										<img
											:src="headerLogoUrl"
											alt=""
											@error="headerLogoError = true"
										/>
									</div>
									<div>
										<h1 class="h5 mb-0">{{ t("app_name") }}</h1>
										<small class="text-muted">{{ org.name }}</small>
									</div>
								</div>
								<div
									class="btn-group btn-group-sm"
									role="group"
									:aria-label="t('lang_toggle')"
								>
									<button
										type="button"
										class="btn"
										:class="
											store.language === 'en'
												? 'btn-primary'
												: 'btn-outline-primary'
										"
										@click="setLang('en')"
									>
										EN
									</button>
									<button
										type="button"
										class="btn"
										:class="
											store.language === 'es'
												? 'btn-primary'
												: 'btn-outline-primary'
										"
										@click="setLang('es')"
									>
										ES
									</button>
								</div>
							</header>

							<div class="mc-screen">
								<component :is="Component" :token="token" />
							</div>
						</div>
					</div>
				</template>
			</template>
		</LoadingOverlay>
	</router-view>

	<!-- Save/upload failure toast (never blocks the user) -->
	<div v-if="toastVisible" class="mc-toast" role="alert">
		<i class="bi bi-exclamation-triangle"></i>
		<span>{{ toastMessage }}</span>
		<button
			type="button"
			class="btn-close btn-close-white"
			aria-label="Close"
			@click="toastVisible = false"
		></button>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import LoadingOverlay from "@/components/LoadingOverlay.vue";
import { type CopyKey, useOrg, useT } from "@/rmo/content/mosquito/copy";
import { useRoutes } from "@/rmo/route/use";
import { useStoreVirtualInspection } from "@/rmo/store/virtualinspection";

interface Props {
	token: string;
}

const props = defineProps<Props>();
const t = useT();
const org = computed(useOrg);
const isLoading = ref(true);
const router = useRouter();
const routes = useRoutes();
const store = useStoreVirtualInspection();
const toastMessage = ref("");
let toastTimer: number | undefined = undefined;
const toastVisible = ref(false);
const headerLogoError = ref(false);
const headerLogoUrl = computed(() => store.state?.logo_url ?? "");
const showHeaderLogo = computed(
	() => !!headerLogoUrl.value && !headerLogoError.value,
);

// Viewport fit: scale the whole wizard so it never needs page scroll.
// Content is designed against a ~720x390 base; the visible viewport is
// measured (visualViewport accounts for the mobile URL bar and keyboard)
// and the fit wrapper is scaled proportionally to match.
const shellRef = ref<HTMLElement | null>(null);
const fitRef = ref<HTMLElement | null>(null);
const scale = ref(1);
const FIT_BASE_H = 720;
const FIT_BASE_W = 390;

function applyViewport() {
	const vv = window.visualViewport;
	const vh = vv ? vv.height : window.innerHeight;
	const vw = vv ? vv.width : window.innerWidth;
	const s = Math.min(
		1,
		Math.max(0.72, vh / FIT_BASE_H),
		Math.max(0.72, vw / FIT_BASE_W),
	);
	scale.value = s;
	if (shellRef.value) {
		shellRef.value.style.height = `${vh}px`;
	}
	if (fitRef.value) {
		fitRef.value.style.height = `${vh / s}px`;
		fitRef.value.style.transform = `scale(${s})`;
	}
}

function onViewportChange() {
	applyViewport();
}

watch(
	() => store.saveError,
	(key) => {
		if (!key) {
			return;
		}
		toastMessage.value = t(key as CopyKey);
		toastVisible.value = true;
		store.saveError = null;
		if (toastTimer) {
			window.clearTimeout(toastTimer);
		}
		toastTimer = window.setTimeout(() => {
			toastVisible.value = false;
		}, 5000);
	},
);

function setLang(lang: "en" | "es") {
	store.setLanguage(lang);
	store.saveLanguage(props.token, lang);
}

onMounted(async () => {
	applyViewport();
	const vv = window.visualViewport;
	if (vv) {
		vv.addEventListener("resize", onViewportChange);
		vv.addEventListener("scroll", onViewportChange);
	}
	window.addEventListener("resize", onViewportChange);
	window.addEventListener("orientationchange", onViewportChange);
	const s = await store.fetchState(props.token).catch(() => null);
	if (s && s.status === "submitted") {
		router.replace(routes.MosquitoCheckComplete(props.token));
	}
	isLoading.value = false;
	// Re-measure once content is in place (browser chrome may have settled).
	requestAnimationFrame(applyViewport);
});

// Navigating between rounds (e.g. "Do this check again") changes the token on
// the same route component; reload the state for the new token.
watch(
	() => props.token,
	async (token) => {
		const s = await store.fetchState(token).catch(() => null);
		if (s && s.status === "submitted") {
			router.replace(routes.MosquitoCheckComplete(token));
		}
	},
);

onBeforeUnmount(() => {
	const vv = window.visualViewport;
	if (vv) {
		vv.removeEventListener("resize", onViewportChange);
		vv.removeEventListener("scroll", onViewportChange);
	}
	window.removeEventListener("resize", onViewportChange);
	window.removeEventListener("orientationchange", onViewportChange);
});
</script>
